import React, { FC, Fragment, useRef } from 'react';
import {
  Flex,
  Modal,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  Box,
  Text,
  Grid,
  Button,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { FieldArray, Form, Formik, FormikProps, FormikValues } from 'formik';
import {
  initialValues,
  validationSchema,
} from './EditWashhubInventoryModal.utils';
import { EditWashhubInventoryModalProps } from './EditWashhubInventoryModal.props';
import FormikTextArea from 'components/primitives/FormikTextArea';
import FormikInput from 'components/primitives/FormikInput';
import FormikSelectInput from 'components/primitives/FormikSelectInput';
import FormikDatepicker from 'components/primitives/FormikDatepicker';
import { EquipmentCountSection } from 'components/modules/EquipmentCountSection';

const EditWashhubInventoryModal: FC<EditWashhubInventoryModalProps> = (
  props,
) => {
  const { isOpen, onClose, onSubmit, washHubs, isLoading, count } = props;

  const formikRef = useRef<FormikProps<FormikValues>>(null);

  const maxVisibleRows = 5;

  const scrollContainerStyle = {
    maxHeight: `${maxVisibleRows * 49}px`, // Assuming each row is ~40px tall
    overflowY: 'auto' as 'auto', // Explicitly typing it as "auto"
  };

  return (
    <Modal
      size="6xl"
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="30px">
        <ModalCloseButton _focus={{ outline: 'none' }} />
        <ModalHeader textAlign="center" fontWeight="500">
          Edit Hub Inventory Count
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" px={6} pb={6}>
            <Formik
              innerRef={formikRef}
              onSubmit={onSubmit}
              initialValues={initialValues(count)}
              validationSchema={validationSchema}>
              {({ values, setFieldValue }) => {
                return (
                  <Form noValidate>
                    {/* Section 1 */}
                    <Box mb={6}>
                      <Flex justify="space-between">
                        <FormikSelectInput
                          containerProps={{ w: '30%' }}
                          placeholder="Select Wash Hub"
                          label="Wash Hub(*)"
                          name="washHub"
                          isReadOnly
                          labelProps={{ mb: 2 }}>
                          {washHubs?.map((s) => (
                            <option key={s._id} value={s._id}>
                              {s?.name || ''}
                            </option>
                          ))}
                        </FormikSelectInput>
                        <FormikDatepicker
                          containerProps={{ w: '30%' }}
                          name="date"
                          label="Count Date(*)"
                          labelProps={{ mb: 2 }}
                        />
                      </Flex>
                    </Box>

                    <Fragment>
                      {isLoading && (
                        <Box pos="absolute" inset="0" bg="bg/80" zIndex={999}>
                          <Center h="full">
                            <Spinner bg="#fff" size="lg" />
                          </Center>
                        </Box>
                      )}

                      {/* Section 2: Full Packages */}
                      <Box mb={6}>
                        <Text fontSize="lg" fontWeight="bold" mb={4}>
                          Full Packages
                        </Text>
                        <Box style={scrollContainerStyle}>
                          <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                            <Text fontWeight="semibold">Package SKUs</Text>
                            <Text fontWeight="semibold">
                              Full Case Count(*)
                            </Text>
                            <Text fontWeight="semibold">Total Packed</Text>
                          </Grid>
                          <FieldArray name="fullPackages">
                            {() =>
                              values.fullPackages.map(
                                (pkg: any, index: any) => (
                                  <Grid
                                    key={index}
                                    templateColumns="repeat(3, 1fr)"
                                    gap={4}
                                    mt={2}>
                                    <FormikInput
                                      name={`fullPackages.${index}.sku`}
                                      isReadOnly
                                      containerProps={{ display: 'none' }}
                                      value={pkg.sku}
                                    />

                                    <FormikInput
                                      name={`fullPackages.${index}.skuName`}
                                      isReadOnly
                                      value={pkg.skuName}
                                    />
                                    <FormikInput
                                      type="number"
                                      name={`fullPackages.${index}.caseCount`}
                                      onChange={(value) => {
                                        const newValue = value
                                          ? Number(value) * pkg.totalQuantity
                                          : 0; // Handle empty case

                                        setFieldValue(
                                          `fullPackages.${index}.totalItems`,
                                          newValue,
                                        );
                                      }}
                                    />
                                    <FormikInput
                                      name={`fullPackages.${index}.totalItems`}
                                      isReadOnly
                                      value={pkg.totalItems}
                                    />
                                  </Grid>
                                ),
                              )
                            }
                          </FieldArray>
                        </Box>
                      </Box>

                      {/* Section 3: In Boxes */}
                      <Box mb={6}>
                        <Text fontSize="lg" fontWeight="bold" mb={2}>
                          New In Boxes
                        </Text>
                        <Box style={scrollContainerStyle}>
                          <Grid templateColumns="repeat(4, 1fr)" gap={4}>
                            <Text fontWeight="bold">Items SKUs</Text>
                            <Text fontWeight="bold">Box Count(*)</Text>
                            <Text fontWeight="bold">Items Per Box</Text>
                            <Text fontWeight="bold">Total in Boxes</Text>
                          </Grid>
                          <FieldArray name="inBoxes">
                            {() =>
                              values.inBoxes.map((box: any, index: any) => (
                                <Grid
                                  key={index}
                                  templateColumns="repeat(4, 1fr)"
                                  gap={4}
                                  mt={2}>
                                  <FormikInput
                                    name={`inBoxes.${index}.sku`}
                                    isReadOnly
                                    containerProps={{ display: 'none' }}
                                    value={box.sku}
                                  />
                                  <FormikInput
                                    name={`inBoxes.${index}.skuName`}
                                    isReadOnly
                                    value={box.skuName}
                                  />
                                  <FormikInput
                                    name={`inBoxes.${index}.boxCount`}
                                    type="number"
                                    onClick={(e) =>
                                      (e.target as HTMLInputElement).select()
                                    }
                                    onChange={(value) => {
                                      const newValue =
                                        Number(value) *
                                        values.inBoxes[index].itemsPerBox;
                                      setFieldValue(
                                        `inBoxes.${index}.totalItems`,
                                        newValue,
                                      );
                                    }}
                                  />
                                  <FormikInput
                                    name={`inBoxes.${index}.itemsPerBox`}
                                    type="number"
                                    onClick={(e) =>
                                      (e.target as HTMLInputElement).select()
                                    }
                                    onChange={(value) => {
                                      const newValue =
                                        Number(value) *
                                        values.inBoxes[index].boxCount;
                                      setFieldValue(
                                        `inBoxes.${index}.totalItems`,
                                        newValue,
                                      );
                                    }}
                                  />
                                  <FormikInput
                                    name={`inBoxes.${index}.totalItems`}
                                    type="number"
                                    isReadOnly
                                    value={
                                      (
                                        box.boxCount * box.itemsPerBox
                                      ).toString() || '0'
                                    }
                                  />
                                </Grid>
                              ))
                            }
                          </FieldArray>
                        </Box>
                      </Box>

                      {/* Section 4: In Process */}
                      <Box mb={6}>
                        <Text fontSize="lg" fontWeight="bold" mb={2}>
                          Loose & In Process
                        </Text>
                        <Text fontSize="sm" mb={4}>
                          Partial packages, partial boxes, dirty, dry in racks,
                          waiting QA, etc.
                        </Text>
                        <Box style={scrollContainerStyle}>
                          <Grid templateColumns="repeat(5, 1fr)" gap={4}>
                            <Text fontWeight="bold">Item SKUs</Text>
                            <Text fontWeight="bold">Open Item Count(*)</Text>
                            <Text fontWeight="bold">Packed</Text>
                            <Text fontWeight="bold">Boxes</Text>
                            <Text fontWeight="bold">Total Items</Text>
                          </Grid>
                          <FieldArray name="inProcess">
                            {() =>
                              values.inProcess.map(
                                (process: any, index: any) => {
                                  // packed items (sum of all fullPackages totalItems for this SKU)
                                  const packedCount = values.fullPackages
                                    .filter((pkg: any) => {
                                      // Check if any item in the package matches the process SKU
                                      return Object.values(
                                        pkg.items || {},
                                      ).some(
                                        (item: any) => item.sku === process.sku,
                                      );
                                    })
                                    .reduce((total: number, pkg: any) => {
                                      // Find the matching item in the package
                                      const matchingItem: any = Object.values(
                                        pkg.items || {},
                                      ).find(
                                        (item: any) => item.sku === process.sku,
                                      );
                                      // Multiply the item quantity by the case count
                                      return (
                                        total +
                                        (matchingItem?.quantity || 0) *
                                          pkg.caseCount
                                      );
                                    }, 0);

                                  //sum of all inBoxes totalItems for this SKU
                                  const boxesCount = values.inBoxes
                                    .filter(
                                      (box: any) => box.sku === process.sku,
                                    )
                                    .reduce(
                                      (total: number, box: any) =>
                                        total + Number(box.totalItems || 0),
                                      0,
                                    );

                                  //Total Items (Open + Packed + Boxes)
                                  const totalItems =
                                    Number(process.itemCount || 0) +
                                    packedCount +
                                    boxesCount;
                                  return (
                                    <Grid
                                      key={index}
                                      templateColumns="repeat(5, 1fr)"
                                      gap={4}
                                      mt={2}>
                                      <FormikInput
                                        name={`inProcess.${index}.sku`}
                                        isReadOnly
                                        containerProps={{ display: 'none' }}
                                        value={process.sku}
                                      />
                                      <FormikInput
                                        name={`inProcess.${index}.skuName`}
                                        isReadOnly
                                        value={process.skuName}
                                      />
                                      <FormikInput
                                        name={`inProcess.${index}.itemCount`}
                                        type="number"
                                        onClick={(
                                          e: React.MouseEvent<HTMLInputElement>,
                                        ) =>
                                          (e.target as HTMLInputElement).select()
                                        }
                                        onInput={(value) =>
                                          setFieldValue(
                                            `inProcess.${index}.itemCount`,
                                            Number(value),
                                          )
                                        }
                                      />
                                      <FormikInput
                                        isReadOnly
                                        name={`inProcess.${index}.packedCount`}
                                        value={packedCount.toString()}
                                      />
                                      <FormikInput
                                        isReadOnly
                                        name={`inProcess.${index}.boxesCount`}
                                        value={boxesCount.toString()}
                                      />
                                      <FormikInput
                                        isReadOnly
                                        name={`inProcess.${index}.totalItems`}
                                        value={totalItems.toString()}
                                      />
                                    </Grid>
                                  );
                                },
                              )
                            }
                          </FieldArray>
                        </Box>
                      </Box>
                    </Fragment>
                    <EquipmentCountSection
                      rows={values.equipment || []}
                      scrollContainerStyle={scrollContainerStyle}
                    />
                    {/* Section 5: Notes */}
                    <Box mb={6}>
                      <FormikTextArea
                        name="notes"
                        isRequired
                        label="Hub Inventory Count Notes(*)"
                      />
                    </Box>

                    {/* Buttons */}
                    <Flex justify="center" gap={4}>
                      <Button type="button" colorScheme="gray">
                        Cancel
                      </Button>
                      <Button type="submit" colorScheme="blue">
                        Save
                      </Button>
                    </Flex>
                  </Form>
                );
              }}
            </Formik>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default EditWashhubInventoryModal;
